// src/components/pages/admin-dash/AdminWebhookLog.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import Icon from "../../ui/Icon";
import AdminTable from "./AdminTable";

/**
 * Read-only viewer for WebhookLogAdminResource (/admin/webhook-log) — raw
 * Stripe/PayPal webhook payloads, including ones that failed signature
 * verification and never reached normal processing. Pure visibility, no
 * reprocessing here (see AdminSubscriptions.jsx's "Reprocess payment" for
 * that). 3-POS_FASE5_ROADMAP.md item 4.12.
 */
const PROVIDER_OPTIONS = [
  { value: "",       label: "All providers" },
  { value: "STRIPE", label: "Stripe" },
  { value: "PAYPAL", label: "PayPal" },
];

const PROCESSED_OPTIONS = [
  { value: "",      label: "All statuses" },
  { value: "true",  label: "Processed" },
  { value: "false", label: "Failed" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const prettyPayload = (raw) => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

export default function AdminWebhookLog() {
  const [provider, setProvider] = useState("");
  const [processed, setProcessed] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchEntries = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (provider) params.provider = provider;
      if (processed !== "") params.processed = processed;
      const res = await axiosInstance.get("/admin/webhook-log", { params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to load webhook log.");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []); // eslint-disable-line

  const columns = [
    { key: "receivedAt", label: "When", sort: true, width: "160px", value: (e) => e.receivedAt || "",
      render: (e) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(e.receivedAt)}</span> },
    { key: "provider", label: "Provider", sort: true, width: "100px", value: (e) => e.provider || "",
      render: (e) => <Badge variant={e.provider === "STRIPE" ? "gold" : "teal"}>{e.provider}</Badge> },
    { key: "eventType", label: "Event type", sort: true, value: (e) => e.eventType || "",
      render: (e) => e.eventType
        ? <span className="text-[12px]" style={{ color: "var(--txt)" }}>{e.eventType}</span>
        : <span className="text-[12px]" style={{ color: "var(--txt-3)" }}>unknown (see error)</span> },
    { key: "processed", label: "Status", width: "100px", value: (e) => e.processed ? 1 : 0,
      render: (e) => e.processed ? <Badge variant="teal">Processed</Badge> : <Badge variant="red">Failed</Badge> },
    { key: "error", label: "Error", render: (e) => (
        <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{e.error || "—"}</span>
      ) },
    { key: "_actions", label: "", align: "right", width: "70px", render: (e) => (
        <button
          type="button"
          title="View payload"
          onClick={(ev) => { ev.stopPropagation(); setSelected(e); }}
          className="inline-flex items-center justify-center rounded-md transition-colors"
          style={{ width: 30, height: 30, color: "var(--txt-2)" }}
          onMouseEnter={(ev) => { ev.currentTarget.style.background = "var(--surf-2)"; ev.currentTarget.style.color = "var(--gold)"; }}
          onMouseLeave={(ev) => { ev.currentTarget.style.background = "transparent"; ev.currentTarget.style.color = "var(--txt-2)"; }}
        >
          <Icon name="code" size={14} />
        </button>
      ) },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Webhook log
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Every Stripe/PayPal webhook payload received, including ones that failed signature
          verification and never reached normal processing.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <SelectField label="Provider" value={provider} onChange={setProvider} options={PROVIDER_OPTIONS} />
          <SelectField label="Status" value={processed} onChange={setProcessed} options={PROCESSED_OPTIONS} />
          <div className="flex items-end">
            <Button icon="search" onClick={fetchEntries} loading={loading}>Search</Button>
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={`${entries.length} recent entries`}
        columns={columns}
        rows={entries}
        rowKey="id"
        loading={loading}
        emptyIcon="webhook"
        emptyMessage="No webhook payloads recorded yet."
        onRowClick={setSelected}
        defaultSort={{ key: "receivedAt", dir: "desc" }}
      />

      {selected && (
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center px-4 py-8"
          style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl fade-in-up max-h-[85vh] flex flex-col"
            style={{
              background: "var(--surf)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-xl)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <div
              className="flex items-start justify-between gap-3 p-5"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={selected.provider === "STRIPE" ? "gold" : "teal"}>{selected.provider}</Badge>
                  {selected.processed
                    ? <Badge variant="teal">Processed</Badge>
                    : <Badge variant="red">Failed</Badge>}
                </div>
                <div className="text-[13px]" style={{ color: "var(--txt)" }}>
                  {selected.eventType || "unknown event type"}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--txt-3)" }}>
                  {fmtDate(selected.receivedAt)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="shrink-0 rounded-md p-1.5"
                style={{ color: "var(--txt-2)" }}
                aria-label="Close"
              >
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {selected.error && (
                <div
                  className="rounded-lg px-3 py-2 text-[12px]"
                  style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
                >
                  {selected.error}
                </div>
              )}
              <pre
                className="text-[11.5px] p-3 rounded-lg overflow-x-auto"
                style={{ background: "var(--surf-2)", color: "var(--txt)", border: "1px solid var(--border)" }}
              >
                {prettyPayload(selected.payload)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{ background: "var(--surf-2)", color: "var(--txt)", borderColor: "var(--border-2)" }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: "var(--txt-3)" }}>▾</span>
      </div>
    </div>
  );
}
